import { capitalize } from 'vue';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import EntityProperty from '@/common/models/EntityProperty';
import { FilterComparisonOperator } from '@/common/models/query-params';
import Filter from '@/common/models/query-params/Filter';
import FilterCollection from '@/common/models/query-params/FilterCollection';
import QueryParams from '@/common/models/query-params/QueryParams';
import ReportDefinition from '@/common/models/reports/ReportDefinition';
import { DASH } from '@/globals';
import { useAccounts, useAdvertisers } from '@/org-app/services/tanstack/queries';
import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import rootStore from '@/store';
import {
  UPDATE_REPORTDEFINITION_DIMENSIONS,
  UPDATE_REPORTDEFINITION_METRICS,
  UPDATE_REPORTDEFINITION_NAME,
} from '@/store/modules/models/mutation-types';
import rootState from '@/store/state';
import i18n from '@/vueI18n';

import ReportDefinitionOptions from './ReportDefinitionOptions';

export const getEntityProperties = () => {
  const type = 'ReportDefinition';

  let isDemandOrganization = false;
  let isCuratorOrganization = false;
  let defaultAdServerName = DASH;
  if (rootState.orgApp != null && rootState.orgApp.organization != null) {
    isDemandOrganization = rootState.orgApp.organization.isDemandOrganization;
    isCuratorOrganization = rootState.orgApp.organization.isCuratorOrganization;
    defaultAdServerName = rootState.orgApp.organizationSettings.defaultAdServerName;
  }

  const getOrganizationSettings = () => rootState.orgApp.organizationSettings;

  const action = (reportdefinition) =>
    rootStore.dispatch('orgApp/updateReportDefinition', { instance: reportdefinition, showLoader: false });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getExtraValidation = (reportdefinition: ReportDefinition) => {
    const extras = {};
    return extras;
  };

  ReportDefinition.entityPropertiesCached = {
    id: new EntityProperty({
      label: capitalize(i18n.global.t('names.reportdefinitionID', { n: 1 })),
      type: TableColumnTypes.string,
      property: 'reportdefinition_id',
      editablePropertyName: 'reportdefinitionID',
    }),
    name: new EntityProperty({
      label: capitalize(i18n.global.t('names.reportdefinition', { n: 1 })),
      property: 'name',
      type: TableColumnTypes.string,
      editablePropertyName: 'name',
      mutation: UPDATE_REPORTDEFINITION_NAME,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    breakdown: new EntityProperty({
      label: capitalize(i18n.global.t('names.breakdown')),
      property: 'breakdown',
      type: TableColumnTypes.string,
      source: () => ReportDefinitionOptions.breakdownOptions(),
      sourceID: 'value',
    }),
    dimensions: new EntityProperty({
      label: capitalize(i18n.global.t('names.dimension', { n: 2 })),
      property: 'dimensions',
      type: TableColumnTypes.list,
      source: () => ReportDefinitionOptions.dimensionOptions(isDemandOrganization),
      sourceID: 'value',
      mutation: UPDATE_REPORTDEFINITION_DIMENSIONS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    metrics: new EntityProperty({
      label: capitalize(i18n.global.t('names.dimension', { n: 2 })),
      property: 'metrics',
      type: TableColumnTypes.list,
      source: () => ReportDefinitionOptions.metricOptions(getOrganizationSettings()),
      sourceID: 'value',
      mutation: UPDATE_REPORTDEFINITION_METRICS,
      editableClassName: type,
      action,
      getExtraValidation,
    }),
    datesRange: new EntityProperty({
      label: capitalize(i18n.global.t('names.dateRange')),
      property: 'datesRange',
      type: TableColumnTypes.string,
      source: () => ReportDefinitionOptions.dateRangeOptions(),
      sourceID: 'value',
    }),
    usesCustomDateRange: new EntityProperty({
      label: capitalize(i18n.global.t('messages.useCustomDateRange')),
      property: 'usesCustomDateRange',
      type: TableColumnTypes.boolean,
    }),
    customDateRange: new EntityProperty({
      label: capitalize(i18n.global.t('names.customDateRange')),
      property: 'customDateRange',
      type: TableColumnTypes.string,
    }),
    emailTargets: new EntityProperty({
      label: capitalize(i18n.global.t('names.email', { n: 2 })),
      property: 'emailTargets',
      type: TableColumnTypes.string,
    }),
    format: new EntityProperty({
      label: capitalize(i18n.global.t('names.outputFormat')),
      property: 'outputFormat',
      type: TableColumnTypes.string,
    }),
    reportSchedulesDescription: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportExecutionSchedule')),
      property: 'reportSchedulesDescription',
      type: TableColumnTypes.string,
    }),
    reportSchedulesTimezone: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportExecutionScheduleTimezone')),
      property: 'reportSchedulesTimezone',
      type: TableColumnTypes.string,
    }),
    filterAccounts: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionAccountFilter')),
      property: 'filterAccounts',
      type: TableColumnTypes.list,
      source: () => runQueryInScope(useAccounts),
    }),
    filterAdvertisers: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionAdvertiserFilter')),
      property: 'filterAdvertisers',
      type: TableColumnTypes.list,
      source: () => runQueryInScope(useAdvertisers),
    }),
    filterBuyers: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionBuyerFilter')),
      property: 'filterBuyers',
      type: TableColumnTypes.list,
      source: () => rootStore.dispatch(isCuratorOrganization ? 'orgApp/getOrganizationDSPs' : 'orgApp/getAllBuyers'),
    }),
    filterBuyingPlatforms: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionBuyingPlatformFilter')),
      property: 'filterBuyingPlatforms',
      type: TableColumnTypes.list,
      source: async ({ queryParams }) => {
        const orgDSPs = (await rootStore.dispatch('exchApp/getOrganizationDSPs', { queryParams })).items;
        return Array.from(new Set(orgDSPs.map((orgDSP) => ({ id: orgDSP.dspId, name: orgDSP.dsp.name }))));
      },
    }),
    filterCampaigns: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionCampaignFilter')),
      property: 'filterCampaigns',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getCampaigns', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('campaign_id', entity.filterCampaigns, FilterComparisonOperator.IN)]),
        ),
    }),
    filterCities: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionCityFilter')),
      property: 'filterCities',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getLocationsAsync', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('location_id', entity.filterCities, FilterComparisonOperator.IN)]),
        ),
    }),
    filterCreatives: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionCreativeFilter')),
      property: 'filterCreatives',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getCreatives', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('creative_id', entity.filterCreatives, FilterComparisonOperator.IN)]),
        ),
    }),
    filterCreativeFiles: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionCreativeFileFilter')),
      property: 'filterCreativeFiles',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getCreativeFiles', queryParams),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([
            new Filter('creativefile_id', entity.filterCreativeFiles, FilterComparisonOperator.IN),
          ]),
        ),
    }),
    filterDeals: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionDealFilter')),
      property: 'filterDeals',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => {
        const route = isCuratorOrganization ? 'orgApp/getCuratorPackages' : 'orgApp/getPackages';
        return rootStore.dispatch(route, { queryParams });
      },
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('package_id', entity.filterDeals, FilterComparisonOperator.IN)]),
        ),
    }),
    filterDealIDs: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionDealIDFilter')),
      property: 'filterDealIDs',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => {
        const route = isCuratorOrganization ? 'orgApp/getCuratorPackages' : 'orgApp/getPackages';
        return rootStore.dispatch(route, { queryParams });
      },
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('package_id', entity.filterDealIDs, FilterComparisonOperator.IN)]),
        ),
    }),
    filterLineItems: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionLineItemFilter')),
      property: 'filterLineItems',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getLineItems', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('lineitem_id', entity.filterLineItems, FilterComparisonOperator.IN)]),
        ),
    }),
    filterMarkets: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionMarketFilter')),
      property: 'filterMarkets',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getMarkets', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('market_id', entity.filterMarkets, FilterComparisonOperator.IN)]),
        ),
    }),
    filterMediaOwners: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionMediaOwnerFilter')),
      property: 'filterMediaOwners',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getMediaOwners', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('mediaowner_id', entity.filterMediaOwners, FilterComparisonOperator.IN)]),
        ),
    }),
    filterMediaTypes: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionMediaTypeFilter')),
      property: 'filterMediaTypes',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getMediaTypes', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('mediatype_id', entity.filterMediaTypes, FilterComparisonOperator.IN)]),
        ),
    }),
    filterMediaTypeChildren: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionMediaTypeChildrenFilter')),
      property: 'filterMediaTypeChildren',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getMediaTypes', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([
            new Filter('mediatype_id', entity.filterMediaTypeChildren, FilterComparisonOperator.IN),
          ]),
        ),
    }),
    filterMediaTypeGrandchildren: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionMediaTypeGrandchildrenFilter')),
      property: 'filterMediaTypeGrandchildren',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getMediaTypes', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([
            new Filter('mediatype_id', entity.filterMediaTypeGrandchildren, FilterComparisonOperator.IN),
          ]),
        ),
    }),
    filterMediaTypeParents: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionMediaTypeParentFilter')),
      property: 'filterMediaTypeParents',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getMediaTypes', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([
            new Filter('mediatype_id', entity.filterMediaTypeParents, FilterComparisonOperator.IN),
          ]),
        ),
    }),
    filterNetworks: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionNetworkFilter')),
      property: 'filterNetworks',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getNetworks', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('network_id', entity.filterNetworks, FilterComparisonOperator.IN)]),
        ),
    }),
    filterRegions: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionRegionFilter')),
      property: 'filterRegions',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getLocationsAsync', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(
          new FilterCollection([new Filter('location_id', entity.filterRegions, FilterComparisonOperator.IN)]),
        ),
    }),
    filterRevenueSources: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionRevenueSourceFilter')),
      property: 'filterRevenueSources',
      type: TableColumnTypes.list,
      source: () => [
        { id: 'AD_SERVER', name: defaultAdServerName },
        { id: 'SSP', name: 'SSP' },
        { id: 'EXCHANGE', name: 'Hivestack Exchange' },
      ],
    }),
    filterSites: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionSiteFilter')),
      property: 'filterSites',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getSites', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(new FilterCollection([new Filter('site_id', entity.filterSites, FilterComparisonOperator.IN)])),
    }),
    filterUnits: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionUnitFilter')),
      property: 'filterUnits',
      type: TableColumnTypes.list,
      source: ({ queryParams }) => rootStore.dispatch('orgApp/getUnits', { queryParams }),
      sourceQueryParamsForReadOnly: (entity: ReportDefinition) =>
        QueryParams.all(new FilterCollection([new Filter('unit_id', entity.filterUnits, FilterComparisonOperator.IN)])),
    }),
    filterCreativeFileHeights: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionCreativeFileHeightFilter')),
      property: 'filterCreativeFileHeights',
      type: TableColumnTypes.list,
    }),
    filterCreativeFileWidths: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionCreativeFileWidthFilter')),
      property: 'filterCreativeFileWidths',
      type: TableColumnTypes.list,
    }),
    filterOthers: new EntityProperty({
      label: capitalize(i18n.global.t('messages.reportDefinitionOtherFilters')),
      property: 'filterOthers',
      type: TableColumnTypes.list,
    }),
  };

  return ReportDefinition.entityPropertiesCached;
};
